import type { AudioEvent, SoundClass } from './events.js';

export interface RegistryEntry {
  /** Path under `packages/client/public/`. `null` = wired but no sample
   *  shipped this round (engine treats as silent no-op). */
  src: string | null;
  class: SoundClass;
  /** 0..1 multiplier. Default 1.0 if omitted. Multiplied by the per-class
   *  preference volume at play time. Placeholders below; pin during
   *  sample sourcing. */
  baseVolume?: number;
  /** If true, the sample plays on a loop until explicitly stopped. Used for
   *  the lobby bed and the end-screen cadences (Bookend Rule, SOUND.md). */
  loop?: boolean;
}

/**
 * Single source of truth: which sample plays at which game moment.
 * To leave a moment silent in the playtest, set its `src` to `null`.
 *
 * Two pairs share a sample by SOUND.md doctrine (§4 Page-Turn,
 * §2 Otherworldly voice) but are surfaced as separate keys so the
 * registry reads as a list of moments rather than a list of samples.
 */
export const SOUND_REGISTRY: Record<AudioEvent, RegistryEntry> = {
  // ambient
  lobby:              { src: '/audio/lobby.mp3',         class: 'music',  baseVolume: 0.35 },

  // structural beats (the page-turn sample is shared across three moments —
  // chapbook opens, role-card flips, book closes on village-win)
  phaseChange:        { src: '/audio/phase-change.wav',  class: 'sounds', baseVolume: 0.7 },
  enterGame:          { src: '/audio/page-turn.mp3',     class: 'sounds', baseVolume: 0.5 },
  roleRevealFlip:     { src: '/audio/page-turn.mp3',     class: 'sounds', baseVolume: 0.5 },
  villageWinFlip:     { src: '/audio/page-turn.mp3',     class: 'sounds', baseVolume: 0.5 },

  // role-reveal trio
  villagerEliminated: { src: '/audio/villager-out.mp3',  class: 'sounds', baseVolume: 0.65 },
  ghostEliminated:    { src: '/audio/otherworldly.mp3',  class: 'sounds', baseVolume: 0.6 },  // shares sample with nightFall
  foolEliminated:     { src: '/audio/fool-out.mp3',      class: 'sounds', baseVolume: 0.55 },

  // night + time
  nightFall:          { src: '/audio/otherworldly.mp3',  class: 'sounds', baseVolume: 0.6 },  // shares sample with ghostEliminated
  lowTime:            { src: '/audio/low-time.mp3',      class: 'sounds', baseVolume: 0.5 },

  // per-player cue (private, non-revealing — SOUND.md Hot-Mic analysis: clue turn order isn't role-revealing)
  yourClueTurn:       { src: '/audio/your-turn.wav',     class: 'sounds', baseVolume: 0.6 },

  // end cadences (looped per the Bookend Rule; only ghostWin has a sample shipped)
  villageWin:         { src: '/audio/village-win.mp3',   class: 'music',  baseVolume: 0.7,  loop: true },
  ghostWin:           { src: '/audio/ghost-win.ogg',     class: 'music',  baseVolume: 0.7,  loop: true },
  stalemate:          { src: '/audio/stalemate.mp3',     class: 'music',  baseVolume: 0.65, loop: true },
};
